import {
  EQUIPMENT,
  getFoundExercisesSelector,
  loadExercisesAction
} from '@gymaholic/packages'
import { LoadingIcon } from 'components/Icon/icons/loading'
import { View } from 'components/View'
import formatMessageIntlUtil from 'modules/intl/utils/format-message'
import { isExercisesLoadingSelector } from 'modules/training/modules/training-exercises/selectors'
import React from 'react'
import { defineMessages } from 'react-intl'
import useIntl from 'modules/component/utils/hooks/useIntl'
import useDispatch from 'modules/component/utils/hooks/useDispatch.js'
import { FixedSizeList } from 'react-window'
import useSelector from 'modules/component/utils/hooks/useSelector.js'
import SimilarExercise from 'pages/Exercise/components/Variations/SimilarExercise'
import isSmallScreenViewportSelect from 'modules/viewport/selectors/is-small-screen'
import TextTitleCondensed from 'components/TextTitleCondensed.js'

const messages = defineMessages({
  exerciseVariations: {
    id: 'ExerciseVariations.variations',
    defaultMessage: '{exerciseName} variations'
  },
  variationsWithDifferentEquipment: {
    id: 'ExerciseVariations.variationsWithDifferentEquipment',
    defaultMessage: '{exerciseName} variations with different equipment'
  }
})

export default function ExerciseVariations ({
  equipment,
  muscles,
  exerciseName,
  exerciseId
}) {
  const intl = useIntl()
  const [loadVariations, setLoadVariations] = React.useState(true)
  const [loadExerciseVariationsWithSameMuscleGroup, setLoadExerciseVariationsWithSameMuscleGroup] = React.useState(true)
  const isSmallScreen = useSelector(isSmallScreenViewportSelect)
  const dispatch = useDispatch()
  const equipmentIds = Object.values(EQUIPMENT)
    .filter(function (obj) {
      return equipment.indexOf(obj) === -1
    })
  const exercisesWithSameMuscleAndSameEquipment = useSelector(
    getFoundExercisesSelector({
      equipmentFilters: equipment,
      muscleFilters: muscles,
      typesFilters: []
    }))
    .filter(id => id !== Number(exerciseId))

  const exercisesWithSameMuscleNotSameEquipment = useSelector(
    getFoundExercisesSelector({
      muscleFilters: muscles,
      equipmentFilters: equipmentIds,
      typesFilters: []
    }))
    .filter(id => id !== Number(exerciseId))

  const isExercisesLoading = useSelector(isExercisesLoadingSelector())
  const sameMuscleSameEquipmentListRef = React.useRef()
  const sameMuscleNotSameEquipmentListRef = React.useRef()

  React.useEffect(() => {
    if (muscles && loadExerciseVariationsWithSameMuscleGroup) {
      dispatch(loadExercisesAction({
        shouldExcludeIds: true,
        query: { muscles: muscles[0] }
      }))
      setLoadExerciseVariationsWithSameMuscleGroup(false)
    }
  }, [muscles])

  React.useEffect(() => {
    if (!loadExerciseVariationsWithSameMuscleGroup && !isExercisesLoading) {
      if (equipment && muscles && loadVariations) {
        dispatch(loadExercisesAction({
          shouldExcludeIds: true,
          query: {
            equipment: equipment[0],
            muscles: muscles[0]
          }
        }))
        setLoadVariations(false)
      }
    }
  }, [exercisesWithSameMuscleAndSameEquipment])

  React.useEffect(() => {
    if (sameMuscleSameEquipmentListRef &&
      sameMuscleSameEquipmentListRef.current) {
      sameMuscleSameEquipmentListRef.current.scrollToItem(0)
    }
  }, [exercisesWithSameMuscleAndSameEquipment])

  React.useEffect(() => {
    if (sameMuscleNotSameEquipmentListRef &&
      sameMuscleNotSameEquipmentListRef.current) {
      sameMuscleNotSameEquipmentListRef.current.scrollToItem(0)
    }
  }, [exercisesWithSameMuscleNotSameEquipment])

  if (
    loadVariations ||
    loadExerciseVariationsWithSameMuscleGroup ||
    isExercisesLoading
  ) {
    return (
      <View
        px={isSmallScreen
          ? 1.5
          : 0}
        centered
      >
        <LoadingIcon
          stroke='black'
          style={{
            width: '30px',
            height: '30px'
          }}
        />
      </View>
    )
  }

  return (
    <View mt={2}>
      {!!exercisesWithSameMuscleAndSameEquipment.length && (
        <>
          <View
            px={isSmallScreen
              ? 1.5
              : 0}
          >
            <TextTitleCondensed
              mr
              c='#151D21'
              style={{
                'text-align': 'left'
              }}
            >
              {formatMessageIntlUtil(
                intl,
                messages.exerciseVariations,
                { exerciseName }
              )}
            </TextTitleCondensed>
          </View>

          <View
            row
            mt='10px'
            mb
          >
            <FixedSizeList
              ref={sameMuscleSameEquipmentListRef}
              className='no-scrollbars'
              height={170}
              itemCount={exercisesWithSameMuscleAndSameEquipment.length}
              itemSize={210}
              layout='horizontal'
              width={window.innerWidth}
            >
              {({
                index,
                style
              }) => {
                return (
                  <View
                    pl={isSmallScreen
                      ? 1.5
                      : 0}
                    style={style}
                  >
                    <SimilarExercise
                      key={index}
                      id={exercisesWithSameMuscleAndSameEquipment[index]}
                    />
                  </View>
                )
              }}
            </FixedSizeList>
          </View>
        </>
      )}

      {
        !!exercisesWithSameMuscleNotSameEquipment.length && (
          <View mt={2}>
            <View
              px={isSmallScreen
                ? 1.5
                : 0}
            >
              <TextTitleCondensed
                mr
                c='#151D21'
                style={{
                  'text-align': 'left'
                }}
              >
                {
                  formatMessageIntlUtil(
                    intl,
                    messages.variationsWithDifferentEquipment,
                    { exerciseName }
                  )
                }
              </TextTitleCondensed>
            </View>
            <View
              row
              mt='10px'
              mb
            >
              <FixedSizeList
                ref={sameMuscleNotSameEquipmentListRef}
                className='no-scrollbars'
                height={170}
                itemCount={exercisesWithSameMuscleNotSameEquipment.length}
                itemSize={210}
                layout='horizontal'
                width={window.innerWidth}
              >
                {({
                  index,
                  style
                }) => {
                  return (
                    <View
                      pl={isSmallScreen
                        ? 1.5
                        : 0}
                      style={style}
                    >
                      <SimilarExercise
                        key={index}
                        id={exercisesWithSameMuscleNotSameEquipment[index]}
                      />
                    </View>
                  )
                }}
              </FixedSizeList>
            </View>
          </View>
        )
      }
    </View>
  )
}
